import type * as type from "@vTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import CommentInput from "./CommentInput";
import { getCalendarShiftTypeFontSize } from "@/utils/fontSizeUtils";

interface SelectCalendarEntry {
  type: string;
  comment: string | null;
}

interface Type_SelectCalendar {
  [date: string]: SelectCalendarEntry;
}

interface CardDateProps {
  day: number;
  holiday: { weekend: number; isHol: boolean };
  dateStr: string;
  availableShifts: type.Type_availableShifts[];
  shift: { type: string; comment: string | null } | null;
  setSelectedCalendarData: (
    value:
      | Type_SelectCalendar
      | ((prev: Type_SelectCalendar) => Type_SelectCalendar)
  ) => void;
  selectedShiftType: string;
  submittedStatus: string;
  isExistWanted: boolean;
  /** 새로 입력된 근무코드 표시 (파란 테두리) */
  isNew?: boolean;
  /** 사유 입력 기능 활성화 여부 */
  enableCommentInput?: boolean;
  onShiftChangeRequest?: (dateStr: string, shiftType: string) => void;
  /** 현재 값 / 최대 값 표시용 */
  currentCount?: number;
  maxCount?: number;
  onDateClick?: (dateStr: string) => void;
}

const WEEKLY_OFF_COMMENT = "주휴";

function Card_date({
  day,
  holiday,
  dateStr,
  availableShifts,
  shift,
  setSelectedCalendarData,
  selectedShiftType,
  submittedStatus,
  isExistWanted,
  isNew = false,
  enableCommentInput = false,
  onShiftChangeRequest,
  currentCount = 0,
  maxCount,
  onDateClick,
}: CardDateProps) {
  // 원티드 작성 > 사유작성을 위한 상태들
  const [isFocused, setIsFocused] = useState(false);
  const [commentValue, setCommentValue] = useState(shift?.comment || "");
  const cardRef = useRef<HTMLDivElement>(null);

  const isWeeklyOff = shift?.comment === WEEKLY_OFF_COMMENT;

  const bgColor =
    shift?.type === "주"
      ? "#D6D6DE"
      : shift
        ? availableShifts.find((item) => item.shift_id === shift.type)?.color
        : "";

  const hasSavedCommentValue = useMemo(() => {
    return !!shift?.comment && !isWeeklyOff;
  }, [shift?.comment, isWeeklyOff]);

  useEffect(() => {
    // 편집 중이 아닐 때만 외부 데이터와 동기화
    if (!isFocused) {
      setCommentValue(shift?.comment || "");
    }
  }, [shift?.comment, isFocused]);

  // 외부 클릭 감지
  useEffect(() => {
    if (!isFocused) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFocused]);

  // 사유 저장
  const handleSaveComment = () => {
    if (!shift?.type) return;

    setSelectedCalendarData((prev) => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        comment: commentValue || null,
      },
    }));
  };

  // 사유 삭제
  const handleDelete = () => {
    if (!shift?.type) return;

    setCommentValue("");
    setSelectedCalendarData((prev) => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        comment: null,
      },
    }));

    setIsFocused(false);
  };

  // 주휴는 회색 bg로 보여주고 주휴로 보여주기 위해 프론트에서
  const shiftText = shift?.type === "주" ? "주휴" : shift?.type;
  const marginTop = shift?.type === "주" ? "4px" : "";
  const fontSize =
    shift?.type === "주"
      ? "25px"
      : shift?.type
        ? getCalendarShiftTypeFontSize(shift.type)
        : undefined;

  return (
    <div ref={cardRef}>
      <div
        className={`w_selectCalendar_date group relative font-pretendard font-medium !text-[15px] text-text-sub 
                ${holiday.weekend === 6 ? "!text-text-sat" : holiday.weekend === 0 ? "!text-text-sun" : ""}
                ${holiday.isHol && "!text-text-holiday"}
                ${isExistWanted && shift?.type && "!text-white"}
                
                ${(submittedStatus === "closed" || isWeeklyOff) && "!cursor-default"}
                `}
      >
        <div className="calendar-date !p-1 !top-[1px] !left-[1px]">{day}</div>
        {isExistWanted ? (
          <>
            <button
              type="button"
              className={`w_selectCalendar_shiftButton ${submittedStatus === "closed" || isWeeklyOff ? "!cursor-default !shadow-none !hover:shadow-none" : ""} ${shift && !isWeeklyOff && isNew ? "!border-[3px] !border-[#0E59C6] !rounded-[8px]" : ""}`}
              style={{ backgroundColor: bgColor }}
              onClick={async () => {
                if (onDateClick) {
                  onDateClick(dateStr);
                }
              }}
            >
              <span
                style={{ fontSize, marginTop }}
                className="truncate px-2 text-3xl!"
              >
                {shiftText}
              </span>
            </button>
            {shift?.type && shift.type !== "주" && shift?.comment && (
              <div className="whitespace-nowrap font-pretendard text-[14px] absolute bottom-[2px] left-[5px] select-none pointer-events-none">
                {shift?.comment && shift.comment.length > 4
                  ? `${shift.comment.slice(0, 4)}...`
                  : shift?.comment}
              </div>
            )}

            {/* 오른쪽 하단 카운트 표시 - 주휴, 근무코드가 존재하면 카운트는 안보이게 */}
            {shift?.type !== "주" &&
              maxCount !== undefined &&
              !shift?.comment &&
              !shift?.type && (
                <div className="absolute bottom-[2px] right-[4px] text-[#D6D6DE] select-none pointer-events-none">
                  <span className="font-pretendard font-medium text-[20px]">
                    {currentCount}
                  </span>
                  <span className="font-pretendard font-medium text-[12px]">
                    /{maxCount}
                  </span>
                </div>
              )}
          </>
        ) : (
          <div className="w_selectCalendar_shiftButton w_selectCalendar_shiftButton--disabled"></div>
        )}

        {/* 원티드 작성 > 사유 입력 인풋 말풍선 */}
        {shift?.type &&
          shift.type !== "주" &&
          enableCommentInput &&
          (isFocused || hasSavedCommentValue) && (
            <CommentInput
              commentValue={commentValue}
              hasSavedComment={hasSavedCommentValue}
              onCommentChange={setCommentValue}
              onSave={handleSaveComment}
              onDelete={handleDelete}
              onClose={() => setIsFocused(false)}
              onEdit={() => setIsFocused(true)}
              className={
                !isFocused && hasSavedCommentValue
                  ? "!hidden group-hover:!flex !-mb-[-0.5px]"
                  : "!-mb-[-0.5px]"
              }
            />
          )}

        {shift && submittedStatus === "requested" && !isWeeklyOff && (
          <button
            type="button"
            className="w_selectCalendar_delButton"
            onClick={() => {
              setSelectedCalendarData((prev) => {
                const newData = { ...prev };
                delete newData[dateStr];
                return newData;
              });
            }}
          ></button>
        )}
        {shift &&
          submittedStatus === "requested" &&
          !isWeeklyOff &&
          enableCommentInput &&
          !isFocused &&
          !hasSavedCommentValue && (
            <button
              type="button"
              className="absolute bottom-[0px] left-1/2 -translate-x-1/2 bg-[#333333] px-[11px] py-[2px] hidden group-hover:block z-10 whitespace-nowrap transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                setIsFocused(true);
              }}
            >
              <span className="font-pretendard text-[15px] text-white">
                사유입력
              </span>
            </button>
          )}
      </div>
    </div>
  );
}
export default Card_date;
